import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { Clock, Users, MapPin, RefreshCw, AlertTriangle, Zap, X, LogOut, Edit3 } from 'lucide-react';
import { CustomTimePicker } from '../common/CustomTimePicker';
import { BulkAdjustClockInModal } from './BulkAdjustClockInModal';
import { easternWallClockToUTC, isoToEasternDate, isoToEasternTime, resolveClockOut, nextDayConfirmMessage } from '../../utils/easternTime';

interface ClockedInEmployee {
  employee_id: number;
  first_name: string;
  last_name: string;
  nickname: string;
  job_id: number;
  job_name: string;
  job_number: string;
  clock_in: string;
  entry_id: number;
  minutes_clocked_in: number;
}

interface JobGroup {
  job_id: number;
  job_name: string;
  job_number: string;
  employees: ClockedInEmployee[];
}

export const ClockedIn: React.FC = () => {
  const auth = useContext(AuthContext);

  // Admin-only access control
  if (!auth?.user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>Please log in to access this page.</p>
      </div>
    );
  }

  if (auth.user.role !== 'admin') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>This page is only accessible to administrators.</p>
        <p>Your current role: <strong>{auth.user.role}</strong></p>
      </div>
    );
  }

  const [clockedInData, setClockedInData] = useState<ClockedInEmployee[]>([]);
  const [jobGroups, setJobGroups] = useState<JobGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Clock out modal state
  const [showClockOutModal, setShowClockOutModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<ClockedInEmployee | null>(null);
  const [clockOutTime, setClockOutTime] = useState('');
  const [saving, setSaving] = useState(false);

  // Bulk adjust clock-in modal state
  const [showBulkAdjustModal, setShowBulkAdjustModal] = useState(false);
  const [selectedJobForBulkAdjust, setSelectedJobForBulkAdjust] = useState<JobGroup | null>(null);

  const fetchClockedInEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(API_CONFIG.ENDPOINTS.CLOCKED_IN_EMPLOYEES);

      if (response.data.success) {
        const data = response.data.data as ClockedInEmployee[];
        setClockedInData(data);

        // Group by job
        const grouped = groupByJob(data);
        setJobGroups(grouped);
        setLastRefresh(new Date());
      }
    } catch (err: any) {
      console.error('Failed to fetch clocked in employees:', err);
      setError(err.response?.data?.error || 'Failed to load clocked-in employees');
    } finally {
      setLoading(false);
    }
  };

  const groupByJob = (employees: ClockedInEmployee[]): JobGroup[] => {
    const jobMap = new Map<number, JobGroup>();

    employees.forEach(emp => {
      const jobId = emp.job_id || 0;
      const jobName = emp.job_name || 'No Job Assigned';
      const jobNumber = emp.job_number || 'N/A';

      if (!jobMap.has(jobId)) {
        jobMap.set(jobId, {
          job_id: jobId,
          job_name: jobName,
          job_number: jobNumber,
          employees: []
        });
      }

      jobMap.get(jobId)!.employees.push(emp);
    });

    // Sort by job name
    return Array.from(jobMap.values()).sort((a, b) =>
      a.job_name.localeCompare(b.job_name)
    );
  };

  const handleClockOutClick = (emp: ClockedInEmployee) => {
    setSelectedEmployee(emp);
    // Default to the current Eastern wall-clock time (not the viewer's local).
    setClockOutTime(isoToEasternTime(new Date().toISOString()));
    setShowClockOutModal(true);
  };

  const handleBulkAdjustClick = (jobGroup: JobGroup) => {
    setSelectedJobForBulkAdjust(jobGroup);
    setShowBulkAdjustModal(true);
  };

  const handleClockOut = async () => {
    if (!selectedEmployee || !clockOutTime) return;

    // Build clock-out from the clock-in's Eastern calendar date + the selected
    // Eastern wall-clock time, so the result is correct from any timezone.
    // resolveClockOut compares ROUNDED times: same-quarter is a valid 0-hour
    // entry; a genuinely earlier time means next day and must be confirmed.
    const resolved = resolveClockOut(
      selectedEmployee.clock_in,
      easternWallClockToUTC(isoToEasternDate(selectedEmployee.clock_in), clockOutTime)
    );
    if (resolved.nextDay && !window.confirm(nextDayConfirmMessage(clockOutTime, selectedEmployee.clock_in))) {
      return;
    }
    const clockOutDate = resolved.date;

    try {
      setSaving(true);
      setError(null);

      await api.put(API_CONFIG.ENDPOINTS.EDIT_TIME, {
        entry_id: selectedEmployee.entry_id,
        clock_in: selectedEmployee.clock_in,
        clock_out: clockOutDate.toISOString(),
        lunch_minutes: 0,
        shop_time_minutes: 0,
        edit_reason: 'Admin clock out from clocked-in page'
      });

      setShowClockOutModal(false);
      setSelectedEmployee(null);
      await fetchClockedInEmployees();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to clock out employee');
    } finally {
      setSaving(false);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  useEffect(() => {
    fetchClockedInEmployees();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchClockedInEmployees();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    header: {
      marginBottom: '24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap' as const,
      gap: '12px'
    },
    headerLeft: {
      flex: 1
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '8px'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: '14px'
    },
    refreshButton: {
      padding: '10px 16px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500'
    },
    kpiGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    kpiCard: {
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    kpiLabel: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '4px'
    },
    kpiValue: {
      fontSize: '28px',
      fontWeight: 'bold'
    },
    kpiValueGreen: {
      color: '#10b981'
    },
    kpiValueBlue: {
      color: '#3b82f6'
    },
    kpiIcon: {
      width: '40px',
      height: '40px',
      opacity: 0.8
    },
    error: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      color: '#dc2626',
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    emptyState: {
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '48px 20px',
      textAlign: 'center' as const
    },
    emptyIcon: {
      width: '64px',
      height: '64px',
      color: '#9ca3af',
      margin: '0 auto 16px'
    },
    emptyTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '8px'
    },
    emptySubtitle: {
      color: '#6b7280',
      fontSize: '14px'
    },
    jobCard: {
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      overflow: 'hidden',
      marginBottom: '20px'
    },
    jobHeader: {
      padding: '16px 20px',
      borderBottom: '1px solid #f3f4f6',
      backgroundColor: '#f9fafb'
    },
    jobHeaderContent: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '12px'
    },
    jobTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '4px'
    },
    jobSubtitle: {
      fontSize: '14px',
      color: '#6b7280'
    },
    adjustTimesButton: {
      padding: '6px 12px',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: '#f1f5f9',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      color: '#475569',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      whiteSpace: 'nowrap' as const
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const
    },
    tableHeader: {
      backgroundColor: '#f9fafb'
    },
    th: {
      textAlign: 'left' as const,
      padding: '12px 16px',
      fontSize: '12px',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px'
    },
    tr: {
      borderBottom: '1px solid #f3f4f6',
      backgroundColor: 'rgba(249, 115, 22, 0.05)',
      borderLeft: '3px solid rgba(249, 115, 22, 0.3)'
    },
    td: {
      padding: '12px 16px',
      fontSize: '14px',
      color: '#374151'
    },
    employeeName: {
      fontWeight: '600',
      color: '#1f2937'
    },
    clockOutButton: {
      padding: '6px 12px',
      backgroundColor: '#f59e0b',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontWeight: '500'
    },
    autoRefreshNote: {
      textAlign: 'center' as const,
      fontSize: '13px',
      color: '#9ca3af',
      marginTop: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px'
    },
    // Modal styles
    modalBackdrop: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '420px',
      overflow: 'hidden'
    },
    modalHeader: {
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    modalHeaderLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    modalIconWrapper: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '12px',
      padding: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: 'white',
      marginBottom: '4px'
    },
    modalSubtitle: {
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: '14px'
    },
    modalCloseButton: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '8px',
      padding: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    modalBody: {
      padding: '24px'
    },
    infoCard: {
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '20px'
    },
    infoRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '8px'
    },
    infoLabel: {
      color: '#6b7280',
      fontSize: '14px'
    },
    infoValue: {
      color: '#1f2937',
      fontSize: '16px',
      fontWeight: '600',
      marginLeft: '34px'
    },
    infoSubvalue: {
      color: '#6b7280',
      fontSize: '14px',
      marginLeft: '34px',
      marginTop: '4px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px'
    },
    timeInput: {
      width: '100%',
      padding: '14px 16px',
      border: '2px solid rgba(245, 158, 11, 0.5)',
      borderRadius: '12px',
      fontSize: '24px',
      fontWeight: '600',
      textAlign: 'center' as const,
      backgroundColor: '#f9fafb'
    },
    modalActions: {
      display: 'flex',
      gap: '12px'
    },
    cancelButton: {
      flex: 1,
      padding: '14px 20px',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600'
    },
    confirmButton: {
      flex: 1,
      padding: '14px 20px',
      backgroundColor: '#f59e0b',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    loading: {
      textAlign: 'center' as const,
      padding: '60px 20px',
      color: '#6b7280'
    },
    spinner: {
      width: '48px',
      height: '48px',
      border: '4px solid #e5e7eb',
      borderTopColor: '#3b82f6',
      borderRadius: '50%',
      margin: '0 auto 16px',
      animation: 'spin 1s linear infinite'
    }
  };

  if (loading && clockedInData.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner} />
          <p>Loading clocked-in employees...</p>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>Clocked In Employees</h1>
          <p style={styles.subtitle}>Real-time view of currently active employees</p>
        </div>
        <button
          style={{
            ...styles.refreshButton,
            opacity: loading ? 0.7 : 1
          }}
          onClick={fetchClockedInEmployees}
          disabled={loading}
        >
          <RefreshCw size={16} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div style={styles.kpiGrid}>
        <div style={styles.kpiCard}>
          <div>
            <p style={styles.kpiLabel}>Currently Clocked In</p>
            <p style={{ ...styles.kpiValue, ...styles.kpiValueGreen }}>
              {new Set(clockedInData.map(emp => emp.employee_id)).size}
            </p>
          </div>
          <Zap size={40} color="#10b981" style={styles.kpiIcon} />
        </div>

        <div style={styles.kpiCard}>
          <div>
            <p style={styles.kpiLabel}>Active Jobs</p>
            <p style={{ ...styles.kpiValue, ...styles.kpiValueBlue }}>
              {jobGroups.length}
            </p>
          </div>
          <MapPin size={40} color="#3b82f6" style={styles.kpiIcon} />
        </div>

        <div style={styles.kpiCard}>
          <div>
            <p style={styles.kpiLabel}>Last Refreshed</p>
            <p style={{ fontSize: '16px', fontWeight: '500', color: '#1f2937' }}>
              {lastRefresh.toLocaleTimeString(undefined, { timeZone: 'America/New_York' })}
            </p>
          </div>
          <Clock size={40} color="#6b7280" style={styles.kpiIcon} />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={styles.error}>
          <AlertTriangle size={20} />
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && clockedInData.length === 0 && (
        <div style={styles.emptyState}>
          <Clock size={64} style={styles.emptyIcon} />
          <h3 style={styles.emptyTitle}>No Employees Clocked In</h3>
          <p style={styles.emptySubtitle}>All employees are currently clocked out</p>
        </div>
      )}

      {/* Job Groups */}
      {jobGroups.map(jobGroup => (
        <div key={jobGroup.job_id} style={styles.jobCard}>
          <div style={styles.jobHeader}>
            <div style={styles.jobHeaderContent}>
              <div>
                <div style={styles.jobTitle}>
                  <MapPin size={20} color="#3b82f6" />
                  {jobGroup.job_name}
                </div>
                <p style={styles.jobSubtitle}>
                  #{jobGroup.job_number} &bull; {jobGroup.employees.length} employee{jobGroup.employees.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                style={styles.adjustTimesButton}
                onClick={() => handleBulkAdjustClick(jobGroup)}
              >
                <Edit3 size={14} />
                Adjust Times
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.th}>Employee</th>
                  <th style={styles.th}>Clock In</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {jobGroup.employees.map(emp => {
                  const fullName = `${emp.first_name} ${emp.last_name}`;

                  return (
                    <tr key={emp.entry_id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.employeeName}>
                          {emp.nickname ? `${emp.nickname} (${fullName})` : fullName}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {new Date(emp.clock_in).toLocaleString(undefined, { timeZone: 'America/New_York' })}
                      </td>
                      <td style={styles.td}>
                        <button
                          style={styles.clockOutButton}
                          onClick={() => handleClockOutClick(emp)}
                        >
                          <LogOut size={14} />
                          Clock Out
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Auto-refresh note */}
      <div style={styles.autoRefreshNote}>
        <RefreshCw size={14} />
        Page auto-refreshes every 30 seconds
      </div>

      {/* Clock Out Modal */}
      {showClockOutModal && selectedEmployee && (
        <div
          style={styles.modalBackdrop}
          onClick={() => setShowClockOutModal(false)}
        >
          <div
            style={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <div style={styles.modalHeaderLeft}>
                <div style={styles.modalIconWrapper}>
                  <LogOut size={24} color="white" />
                </div>
                <div>
                  <h2 style={styles.modalTitle}>Clock Out</h2>
                  <p style={styles.modalSubtitle}>
                    {selectedEmployee.nickname || `${selectedEmployee.first_name} ${selectedEmployee.last_name}`}
                  </p>
                </div>
              </div>
              <button
                style={styles.modalCloseButton}
                onClick={() => setShowClockOutModal(false)}
              >
                <X size={20} color="white" />
              </button>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              {/* Employee Info Card */}
              <div style={styles.infoCard}>
                <div style={styles.infoRow}>
                  <Clock size={20} color="#f59e0b" />
                  <span style={styles.infoLabel}>Clocked In</span>
                </div>
                <p style={styles.infoValue}>
                  {new Date(selectedEmployee.clock_in).toLocaleString('en-US', {
                    timeZone: 'America/New_York',
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
                <p style={styles.infoSubvalue}>
                  {formatDuration(selectedEmployee.minutes_clocked_in)} elapsed
                </p>
              </div>

              {/* Clock Out Time Input */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Set Clock Out Time</label>
                <CustomTimePicker
                  value={clockOutTime}
                  onChange={(val) => setClockOutTime(val)}
                  label="Select Clock Out Time"
                />
              </div>

              {/* Action Buttons */}
              <div style={styles.modalActions}>
                <button
                  style={styles.cancelButton}
                  onClick={() => setShowClockOutModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  style={{
                    ...styles.confirmButton,
                    opacity: saving || !clockOutTime ? 0.6 : 1,
                    cursor: saving || !clockOutTime ? 'not-allowed' : 'pointer'
                  }}
                  onClick={handleClockOut}
                  disabled={saving || !clockOutTime}
                >
                  {saving ? (
                    <>
                      <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <LogOut size={18} />
                      Clock Out
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Adjust Clock-In Modal */}
      {showBulkAdjustModal && selectedJobForBulkAdjust && (
        <BulkAdjustClockInModal
          isOpen={showBulkAdjustModal}
          onClose={() => {
            setShowBulkAdjustModal(false);
            setSelectedJobForBulkAdjust(null);
          }}
          onSuccess={fetchClockedInEmployees}
          job={{
            job_id: selectedJobForBulkAdjust.job_id,
            job_name: selectedJobForBulkAdjust.job_name,
            job_number: selectedJobForBulkAdjust.job_number
          }}
          employees={selectedJobForBulkAdjust.employees}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
